import type { ReportInfo } from "@/components/features/workspace/navbar/types";

export type AskProjectReportListItem = {
  id: string;
  title: string;
  status: string;
};

type CacheEntry<TValue> = {
  value: TValue;
  updatedAt: number;
};

export const REPORTS_TTL_MS = 60_000;
export const REPORT_INFO_TTL_MS = 60_000;
export const MAX_PROJECTS = 15;

const reportsCache = new Map<string, CacheEntry<AskProjectReportListItem[]>>();
const reportInfoCache = new Map<string, CacheEntry<Record<string, ReportInfo>>>();
const inFlightReports = new Map<string, Promise<AskProjectReportListItem[]>>();
const inFlightReportInfo = new Map<string, Promise<Record<string, ReportInfo>>>();
const cacheOrder = new Map<string, true>();

function isStale(updatedAt: number, ttlMs: number): boolean {
  return Date.now() - updatedAt > ttlMs;
}

function evictIfNeeded(): void {
  while (cacheOrder.size > MAX_PROJECTS) {
    const oldestProjectId = cacheOrder.keys().next().value as string | undefined;
    if (!oldestProjectId) {
      break;
    }
    cacheOrder.delete(oldestProjectId);
    reportsCache.delete(oldestProjectId);
    reportInfoCache.delete(oldestProjectId);
    inFlightReports.delete(oldestProjectId);
    inFlightReportInfo.delete(oldestProjectId);
  }
}

export function touchAskProjectCache(projectId: string): void {
  if (!projectId) {
    return;
  }

  if (cacheOrder.has(projectId)) {
    cacheOrder.delete(projectId);
  }
  cacheOrder.set(projectId, true);
  evictIfNeeded();
}

export function getCachedProjectReports(projectId: string): {
  data: AskProjectReportListItem[] | null;
  isStale: boolean;
} {
  const entry = reportsCache.get(projectId);
  if (!entry) {
    return { data: null, isStale: true };
  }

  touchAskProjectCache(projectId);
  return {
    data: entry.value,
    isStale: isStale(entry.updatedAt, REPORTS_TTL_MS),
  };
}

export function setCachedProjectReports(
  projectId: string,
  data: AskProjectReportListItem[]
): void {
  reportsCache.set(projectId, {
    value: data,
    updatedAt: Date.now(),
  });
  touchAskProjectCache(projectId);
}

export function getCachedProjectReportInfo(projectId: string): {
  data: Record<string, ReportInfo> | null;
  isStale: boolean;
} {
  const entry = reportInfoCache.get(projectId);
  if (!entry) {
    return { data: null, isStale: true };
  }

  touchAskProjectCache(projectId);
  return {
    data: entry.value,
    isStale: isStale(entry.updatedAt, REPORT_INFO_TTL_MS),
  };
}

export function setCachedProjectReportInfo(
  projectId: string,
  data: Record<string, ReportInfo>
): void {
  reportInfoCache.set(projectId, {
    value: data,
    updatedAt: Date.now(),
  });
  touchAskProjectCache(projectId);
}

export function mergeCachedProjectReportInfo(
  projectId: string,
  partial: Record<string, ReportInfo>
): Record<string, ReportInfo> {
  const previous = reportInfoCache.get(projectId)?.value ?? {};
  const next = {
    ...previous,
    ...partial,
  };

  reportInfoCache.set(projectId, {
    value: next,
    updatedAt: Date.now(),
  });
  touchAskProjectCache(projectId);
  return next;
}

export function getInFlightProjectReports(
  projectId: string
): Promise<AskProjectReportListItem[]> | undefined {
  return inFlightReports.get(projectId);
}

export function setInFlightProjectReports(
  projectId: string,
  request: Promise<AskProjectReportListItem[]>
): void {
  inFlightReports.set(projectId, request);
}

export function clearInFlightProjectReports(projectId: string): void {
  inFlightReports.delete(projectId);
}

export function getInFlightProjectReportInfo(
  projectId: string
): Promise<Record<string, ReportInfo>> | undefined {
  return inFlightReportInfo.get(projectId);
}

export function setInFlightProjectReportInfo(
  projectId: string,
  request: Promise<Record<string, ReportInfo>>
): void {
  inFlightReportInfo.set(projectId, request);
}

export function clearInFlightProjectReportInfo(projectId: string): void {
  inFlightReportInfo.delete(projectId);
}

export function invalidateAskProjectHoverCache(projectId?: string): void {
  if (!projectId) {
    reportsCache.clear();
    reportInfoCache.clear();
    inFlightReports.clear();
    inFlightReportInfo.clear();
    cacheOrder.clear();
    return;
  }

  reportsCache.delete(projectId);
  reportInfoCache.delete(projectId);
  inFlightReports.delete(projectId);
  inFlightReportInfo.delete(projectId);
  cacheOrder.delete(projectId);
}
